<script setup lang="ts">
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import type { FormatOption } from '@/types/FormatOption.ts';
import { type Component, onMounted, onUnmounted, reactive, watch } from 'vue';
import OutputMarkdownComponent from '@/components/output/OutputMarkdownComponent.vue';
import OutputCodeComponent from '@/components/output/OutputCodeComponent.vue';
import OutputChoiceComponent from '@/components/output/OutputChoiceComponent.vue';
import { PromptService } from '@/services/PromptService.ts';
import { useClientStore } from '@/stores/ClientStore.ts';
import { runPromptAction } from '@/actions/run/RunPromptAction.ts';
import type { SessionId } from '@/types/Session.ts';
import { ProfileConfigService } from '@/services/config/ProfileConfigService.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import { setIntervalImmediately } from '@/utils/RandomUtils.ts';
import PromptStatusBadge from '@/components/output/PromptStatusBadge.vue';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import Loader from '@/components/loader/Loader.vue';

/* region Setup */
const outputComponents: Record<FormatOption, Component> = {
    markdown: OutputMarkdownComponent,
    code: OutputCodeComponent,
    choice: OutputChoiceComponent,
};

const props = defineProps<{
    sessionId: SessionId;
    profileConfigId: ProfileConfig['id'];
    stepConfigId: StepConfig['id'];
    promptConfig: PromptConfig;
    promptIndex: number;
    variableChainHash: string;
}>();

interface ComponentState {
    profileConfig: ProfileConfig;
    stepConfig: StepConfig;
    sessionPromptId: string;
    isPromptSlowInterval: ReturnType<typeof setInterval> | undefined;
    isPromptSlow: boolean;
    mountFinished: boolean;
}

const state: ComponentState = reactive({
    profileConfig: {} as ProfileConfig,
    stepConfig: {} as StepConfig,
    sessionPromptId: '',
    isPromptSlowInterval: undefined,
    isPromptSlow: false,
    mountFinished: false,
});
/* endregion Setup */

/* region Stores */
const clientStore = useClientStore();
/* endregion Stores */

/* region Lifecycle */
watch(
    () => clientStore.sessionVariables[props.sessionId], // @TODO: Check
    async () => {
        void runPromptAction(props.promptConfig.id, props.sessionId);
    },
    { deep: true },
);

onMounted(async () => {
    const profileConfig = ProfileConfigService.getProfileConfig(props.profileConfigId);
    if (!profileConfig) {
        throw new Error(`Could not find profile config with id ${props.profileConfigId}`);
    }

    const stepConfig = StepConfigService.getStepConfig(props.stepConfigId);
    if (!stepConfig) {
        throw new Error(`Could not find step config with id ${props.stepConfigId}`);
    }

    state.profileConfig = profileConfig;
    state.stepConfig = stepConfig;
    state.sessionPromptId = PromptService.getSessionPromptId(
        props.sessionId,
        props.promptConfig.id,
        props.variableChainHash,
    );

    if (state.isPromptSlowInterval === undefined) {
        state.isPromptSlowInterval = setIntervalImmediately(() => {
            const startDate = clientStore.streamStartDates[state.sessionPromptId];
            if (!startDate) {
                return;
            }

            state.isPromptSlow = Date.now() - startDate > 15_000;
        }, 1000);
    }

    void runPromptAction(props.promptConfig.id, props.sessionId);

    state.mountFinished = true;
});

onUnmounted(() => {
    if (state.isPromptSlowInterval !== undefined) {
        clearInterval(state.isPromptSlowInterval);
    }
});
/* endregion Lifecycle */
</script>

<template>
    <component
        v-if="state.mountFinished"
        :is="outputComponents[promptConfig.formatAs]"
        :sessionId="sessionId"
        :profileConfig="state.profileConfig"
        :stepConfig="state.stepConfig"
        :promptConfig="promptConfig"
        :promptIndex="promptIndex"
        :streamId="state.sessionPromptId"
        :content="clientStore.streams[state.sessionPromptId] ?? ''"
        :selectedNavigationIndex="
            clientStore.verticalNavigationSelection[`${sessionId}_${stepConfigId}`] ?? 0
        "
        :variableChainHash="variableChainHash"
        :isPromptSlow="state.isPromptSlow"
    />
    <Card v-else>
        <CardHeader class="px-4 py-2 pe-2 text-lg">
            <CardTitle>
                <div class="flex items-center justify-between text-base">
                    {{ promptConfig.name }}
                    <PromptStatusBadge
                        :prompt-config-id="promptConfig.id"
                        :session-id="sessionId"
                        :variable-chain-hash="variableChainHash"
                    />
                </div>
            </CardTitle>
        </CardHeader>
        <CardContent class="relative p-0 dark:bg-zinc-900/60">
            <div class="flex flex-col items-center justify-center p-5">
                <Loader :width="24" :height="24" />
            </div>
        </CardContent>
    </Card>
</template>
